/**
 * Module: REPORTING-V1 admin API client
 * SpecReferenceID: REPORTING-V1
 * Requirements: REPORTING-V1-R12
 *
 * Typed axios wrappers for the six reporting endpoints plus three CSV
 * export endpoints. Centralising the call surface here (rather than
 * inlining in ReportingPage) keeps the date-range marshalling + response
 * type contracts in one place and lets the page component focus on UI
 * state.
 *
 * Date range policy: every function accepts a UtcDateRange whose `start`
 * and `end` are already ISO-8601 UTC timestamps. Conversion from the
 * admin's locale-picked dates to UTC 00:00 bounds is the DatePresetPicker's
 * responsibility, not this module's — see REPORTING-V1-R12 Date Range
 * Semantics in the implementation plan.
 *
 * Risk Links: R6
 * FMEA Links: FM5
 */

import api from '@/lib/api';

/**
 * A date range expressed as inclusive-start / exclusive-end ISO 8601 UTC
 * timestamps. Matches the backend contract (`WHERE created_at >= :start
 * AND created_at < :end`), so a range covering all of 2026-04-20 UTC is
 * `{ start: "2026-04-20T00:00:00Z", end: "2026-04-21T00:00:00Z" }`.
 */
export interface UtcDateRange {
  start: string;
  end: string;
}

/**
 * REPORTING-V1-R01: one row of the chat-turn data view as returned by
 * `GET /reporting/chat-turns`. Field names are camelCase per the backend's
 * `response_model_by_alias=True` setting.
 */
export interface ChatTurnItem {
  questionId: string;
  responseId: string | null;
  conversationId: string;
  question: string;
  response: string | null;
  feedback: string | null;
  isLiked: boolean | null;
  questionDate: string;
  responseDate: string | null;
  responseTimeSeconds: number | null;
  name: string | null;
  emailAddress: string | null;
  citations: unknown[] | null;
  turnNumber: number;
  conversationLengthSeconds: number;
  userSessionTimeSeconds: number;
}

export interface ChatTurnsResponse {
  items: ChatTurnItem[];
  totalCount: number;
  limit: number;
  offset: number;
}

/** REPORTING-V1-R03/R04: one row of the scored-turn view. */
export interface ScoredTurnItem {
  questionId: string;
  conversationId: string;
  questionDate: string;
  name: string | null;
  question: string;
  response: string | null;
  responseTimeSeconds: number | null;
  hasCitation: boolean | null;
  hasFailurePhrase: boolean | null;
  wasReasked: boolean | null;
  abandonAfterAnswerFlag: boolean | null;
  implicitScore: number | null;
  finalScore: number | null;
}

export interface ScoredTurnsResponse {
  items: ScoredTurnItem[];
  totalCount: number;
  limit: number;
  offset: number;
}

/** REPORTING-V1-R06: classification coverage snapshot for a date range. */
export interface ClassificationStatus {
  totalCount: number;
  classifiedCount: number;
  unclassifiedCount: number;
  startDate: string | null;
  endDate: string | null;
}

/**
 * REPORTING-V1-R06 (Bug #5070): 202 response from `POST /reporting/classify`.
 *
 * The endpoint now schedules a background classification run instead of
 * draining synchronously (a real backlog crossed the 30s request timeout).
 * Poll the run with `getClassificationRun(classificationRunId)` /
 * `waitForClassificationRun` to follow it to completion.
 */
export interface BatchClassificationScheduled {
  classificationRunId: number;
  status: string;
}

/** REPORTING-V1-R06 (Bug #5070): terminal states of a classification run. */
export type ClassificationRunOutcome =
  | 'running'
  | 'success'
  | 'partial_failure'
  | 'failure';

/** REPORTING-V1-R06 (Bug #5070): one `classification_runs` row. */
export interface ClassificationRun {
  id: number;
  outcome: ClassificationRunOutcome;
  triggeredBy: string;
  startedAt: string;
  finishedAt: string | null;
  startDate: string | null;
  endDate: string | null;
  totalScannedCount: number;
  classifiedCount: number;
  failedCount: number;
  errorMessage: string | null;
}

/** REPORTING-V1-R07: one row of daily KPI aggregation. */
export interface DailyKpiItem {
  kpiDate: string;
  turns: number;
  avgFinalTurnScore: number | null;
  reaskRate: number | null;
  abandonRate: number | null;
  failurePhraseRate: number | null;
  citationsRate: number | null;
  noInfoRate: number | null;
  substantiveResponseRate: number | null;
  missingCitationRate: number | null;
  avgResponseTimeSec: number | null;
}

export interface DailyKpisResponse {
  items: DailyKpiItem[];
  startDate: string | null;
  endDate: string | null;
}

/** REPORTING-V1-R07: response from `POST /reporting/kpis/compute`. */
export interface ComputeDailyKpisResponse {
  daysComputed: number;
  daysUpserted: number;
  startDate: string | null;
  endDate: string | null;
}

/**
 * REPORTING-V1-R07 (Bug #5249): 202 response from `POST /reporting/kpis/compute`.
 *
 * The endpoint now schedules a background compute run instead of computing
 * synchronously (a large range crossed the frontend's 30s axios timeout and
 * the browser aborted the request). Poll the run with `getKpiComputeRun` /
 * `waitForKpiComputeRun` to follow it to completion.
 */
export interface KpiComputeScheduled {
  kpiComputeRunId: number;
  status: string;
}

/** REPORTING-V1-R07 (Bug #5249): terminal states of a KPI compute run. */
export type KpiComputeRunOutcome = 'running' | 'success' | 'failure';

/** REPORTING-V1-R07 (Bug #5249): one `kpi_compute_runs` row. */
export interface KpiComputeRun {
  id: number;
  outcome: KpiComputeRunOutcome;
  triggeredBy: string;
  startedAt: string;
  finishedAt: string | null;
  startDate: string | null;
  endDate: string | null;
  daysComputed: number;
  daysUpserted: number;
  errorMessage: string | null;
}

interface PagedParams extends UtcDateRange {
  limit?: number;
  offset?: number;
}

/**
 * REPORTING-V1-R01: List chat turns in a date range.
 */
export async function listChatTurns(
  params: PagedParams,
): Promise<ChatTurnsResponse> {
  const response = await api.get<ChatTurnsResponse>('/reporting/chat-turns', {
    params: {
      start_date: params.start,
      end_date: params.end,
      limit: params.limit ?? 20,
      offset: params.offset ?? 0,
    },
  });
  return response.data;
}

/**
 * REPORTING-V1-R02: Download the chat-turn CSV export.
 * Returns a Blob so the caller can trigger a browser download.
 */
export async function downloadChatTurnsCsv(range: UtcDateRange): Promise<Blob> {
  const response = await api.get<Blob>('/reporting/chat-turns/export', {
    params: { start_date: range.start, end_date: range.end },
    responseType: 'blob',
  });
  return response.data;
}

/**
 * REPORTING-V1-R03/R04: List scored chat turns in a date range.
 */
export async function listScoredTurns(
  params: PagedParams,
): Promise<ScoredTurnsResponse> {
  const response = await api.get<ScoredTurnsResponse>(
    '/reporting/scored-turns',
    {
      params: {
        start_date: params.start,
        end_date: params.end,
        limit: params.limit ?? 20,
        offset: params.offset ?? 0,
      },
    },
  );
  return response.data;
}

/**
 * REPORTING-V1-R04: Download the scored-turn CSV export.
 */
export async function downloadScoredTurnsCsv(
  range: UtcDateRange,
): Promise<Blob> {
  const response = await api.get<Blob>('/reporting/scored-turns/export', {
    params: { start_date: range.start, end_date: range.end },
    responseType: 'blob',
  });
  return response.data;
}

/**
 * REPORTING-V1-R06: Classification coverage snapshot. Returns the total /
 * classified / unclassified counts for the range.
 */
export async function getClassificationStatus(
  range: UtcDateRange,
): Promise<ClassificationStatus> {
  const response = await api.get<ClassificationStatus>(
    '/reporting/classify/status',
    {
      params: { start_date: range.start, end_date: range.end },
    },
  );
  return response.data;
}

/**
 * REPORTING-V1-R06 (Bug #5070): Schedule a background batch-classification
 * run over every still-unclassified user turn in the range. Returns the
 * run id immediately (202). Idempotent — already-classified turns are
 * skipped, so re-running over an overlapping range only touches turns
 * that still need a classification. Follow the run with
 * `waitForClassificationRun(scheduled.classificationRunId)`.
 */
export async function runBatchClassify(
  range: UtcDateRange,
): Promise<BatchClassificationScheduled> {
  const response = await api.post<BatchClassificationScheduled>(
    '/reporting/classify',
    { start_date: range.start, end_date: range.end },
  );
  return response.data;
}

/**
 * REPORTING-V1-R06 (Bug #5070): Fetch the current state of a background
 * classification run. `finishedAt === null` (equivalently
 * `outcome === 'running'`) means it is still in progress.
 */
export async function getClassificationRun(
  runId: number,
): Promise<ClassificationRun> {
  const response = await api.get<ClassificationRun>(
    `/reporting/classify/runs/${runId}`,
  );
  return response.data;
}

/**
 * REPORTING-V1-R06 (Bug #5070): Poll a classification run until it
 * reaches a terminal outcome (success / partial_failure / failure) or
 * the timeout elapses. Returns the final run. The caller decides how to
 * present `outcome` — this resolves (not rejects) on `failure` so the UI
 * can surface `errorMessage`; it only rejects on the poll timeout.
 */
export async function waitForClassificationRun(
  runId: number,
  opts: {
    intervalMs?: number;
    timeoutMs?: number;
    onProgress?: (run: ClassificationRun) => void;
  } = {},
): Promise<ClassificationRun> {
  const intervalMs = opts.intervalMs ?? 2000;
  // Generous default: the backlog cap is small per run but the LLM loop
  // can take several minutes; 15 min is well past the worst observed.
  const timeoutMs = opts.timeoutMs ?? 15 * 60 * 1000;
  const start = Date.now();
  for (;;) {
    const run = await getClassificationRun(runId);
    if (run.outcome !== 'running' || run.finishedAt !== null) {
      return run;
    }
    opts.onProgress?.(run);
    if (Date.now() - start > timeoutMs) {
      throw new Error(
        'Classification run is still in progress after 15 minutes. ' +
          'It may still complete — check the coverage panel shortly.',
      );
    }
    await new Promise((resolve) => setTimeout(resolve, intervalMs));
  }
}

/**
 * REPORTING-V1-R07: List computed daily KPIs for the range.
 */
export async function listDailyKpis(
  range: UtcDateRange,
): Promise<DailyKpisResponse> {
  const response = await api.get<DailyKpisResponse>('/reporting/kpis', {
    params: { start_date: range.start, end_date: range.end },
  });
  return response.data;
}

/**
 * REPORTING-V1-R07 (Bug #5249): Schedule a background daily-KPI compute over
 * every scored turn in the range. Returns the run id immediately (202). The
 * compute itself (score + aggregate + upsert) runs server-side in a detached
 * task. Follow it with `waitForKpiComputeRun(scheduled.kpiComputeRunId)`.
 */
export async function scheduleComputeDailyKpis(
  range: UtcDateRange,
): Promise<KpiComputeScheduled> {
  const response = await api.post<KpiComputeScheduled>(
    '/reporting/kpis/compute',
    { start_date: range.start, end_date: range.end },
  );
  return response.data;
}

/**
 * REPORTING-V1-R07 (Bug #5249): Fetch the current state of a background KPI
 * compute run. `finishedAt === null` (equivalently `outcome === 'running'`)
 * means it is still in progress.
 */
export async function getKpiComputeRun(
  runId: number,
): Promise<KpiComputeRun> {
  const response = await api.get<KpiComputeRun>(
    `/reporting/kpis/compute/runs/${runId}`,
  );
  return response.data;
}

/**
 * REPORTING-V1-R07 (Bug #5249): Poll a KPI compute run until it reaches a
 * terminal outcome (success / failure) or the timeout elapses. Returns the
 * final run. Resolves (not rejects) on `failure` so the UI can surface
 * `errorMessage`; it only rejects on the poll timeout.
 */
export async function waitForKpiComputeRun(
  runId: number,
  opts: {
    intervalMs?: number;
    timeoutMs?: number;
    onProgress?: (run: KpiComputeRun) => void;
  } = {},
): Promise<KpiComputeRun> {
  const intervalMs = opts.intervalMs ?? 2000;
  // Generous default: a full-range compute over a large corpus can take a
  // couple of minutes; 15 min is well past the worst observed.
  const timeoutMs = opts.timeoutMs ?? 15 * 60 * 1000;
  const start = Date.now();
  for (;;) {
    const run = await getKpiComputeRun(runId);
    if (run.outcome !== 'running' || run.finishedAt !== null) {
      return run;
    }
    opts.onProgress?.(run);
    if (Date.now() - start > timeoutMs) {
      throw new Error(
        'KPI compute run is still in progress after 15 minutes. ' +
          'It may still complete — check the KPI table shortly.',
      );
    }
    await new Promise((resolve) => setTimeout(resolve, intervalMs));
  }
}

/**
 * REPORTING-V1-R07 (Bug #5249): Aggregate + upsert daily KPIs for every UTC
 * day in the range, end to end. Schedules the background compute run and
 * polls it to a terminal outcome, so the caller no longer holds a single
 * long request bound by the 30s axios timeout. Throws on a failed run; the
 * caller catches it. Returns the completed run (carries `daysUpserted` /
 * `daysComputed`).
 */
export async function computeDailyKpis(
  range: UtcDateRange,
  opts: { onProgress?: (run: KpiComputeRun) => void } = {},
): Promise<KpiComputeRun> {
  const scheduled = await scheduleComputeDailyKpis(range);
  const run = await waitForKpiComputeRun(scheduled.kpiComputeRunId, {
    onProgress: opts.onProgress,
  });
  if (run.outcome === 'failure') {
    throw new Error(run.errorMessage ?? 'Background KPI compute run failed.');
  }
  return run;
}

/**
 * REPORTING-V1-R08: Download the consolidated daily report CSV
 * (chat turns joined with classifications).
 */
export async function downloadDailyReportCsv(
  range: UtcDateRange,
): Promise<Blob> {
  const response = await api.get<Blob>('/reporting/daily-report/export', {
    params: { start_date: range.start, end_date: range.end },
    responseType: 'blob',
  });
  return response.data;
}

/**
 * Trigger a browser file download for a Blob returned by one of the
 * export wrappers. Kept in this module (rather than a generic helper)
 * because every reporting CSV filename has the same start/end date tag
 * shape — centralising the filename convention here means the page
 * component only has to say "download this".
 */
export function triggerBrowserDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}
